import { useEffect, useState } from "react";
import api from "../lib/api";
import MasterCrud, { StatusBadge } from "../components/MasterCrud";
import { GRADES, OUTLET_FORMATS, VERTICALS, ROLES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

function useLookups(sources) {
  const [lookups, setLookups] = useState({});
  useEffect(() => {
    sources.forEach((src) => {
      api.get(`/${src.endpoint}`).then(({ data }) => {
        setLookups((p) => ({
          ...p,
          [src.name]: data.map((d) => ({ value: d[src.valueKey], label: d[src.labelKey] })),
          [`${src.name}Map`]: Object.fromEntries(data.map((d) => [d[src.valueKey], d[src.labelKey]])),
        }));
      }).catch(() => {});
    });
  }, []); // eslint-disable-line
  return lookups;
}

const statusField = { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"], default: "Active" };

export function ZonesPage() {
  const { can } = useAuth();
  const lookups = useLookups([{ name: "users", endpoint: can("users", "read") ? "users" : "zones", valueKey: "user_id", labelKey: "full_name" }]);
  return (
    <MasterCrud
      title="Zones" subtitle="Operating zones across Central & South Kerala"
      endpoint="zones" idField="zone_id" lookups={lookups}
      columns={[
        { key: "zone_name", label: "Zone" },
        { key: "zonal_head_sales_id", label: "Zonal Head — Sales", render: (r, l) => l.usersMap?.[r.zonal_head_sales_id] || "—" },
        { key: "zonal_head_service_id", label: "Zonal Head — Service", render: (r, l) => l.usersMap?.[r.zonal_head_service_id] || "—" },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      ]}
      fields={[
        { key: "zone_name", label: "Zone name", required: true, fullWidth: true },
        { key: "zonal_head_sales_id", label: "Zonal Head — Sales", type: "select", optionsFrom: "users" },
        { key: "zonal_head_service_id", label: "Zonal Head — Service", type: "select", optionsFrom: "users" },
        statusField,
      ]}
    />
  );
}

export function BranchesPage() {
  const { can } = useAuth();
  const src = [{ name: "zones", endpoint: "zones", valueKey: "zone_id", labelKey: "zone_name" }];
  if (can("users", "read")) src.push({ name: "users", endpoint: "users", valueKey: "user_id", labelKey: "full_name" });
  const lookups = useLookups(src);
  return (
    <MasterCrud
      title="Branches" subtitle="Outlets — 3S, 2S, 1S, workshops and CPO"
      endpoint="branches" idField="branch_id" lookups={lookups}
      columns={[
        { key: "branch_code", label: "Code" },
        { key: "branch_name", label: "Branch" },
        { key: "zone_id", label: "Zone", render: (r, l) => l.zonesMap?.[r.zone_id] || "—" },
        { key: "district", label: "District" },
        { key: "outlet_format", label: "Format" },
        { key: "branch_status", label: "Stage", render: (r) => <StatusBadge value={r.branch_status} /> },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      ]}
      fields={[
        { key: "branch_code", label: "Branch code", required: true },
        { key: "branch_name", label: "Branch name", required: true },
        { key: "zone_id", label: "Zone", type: "select", optionsFrom: "zones", required: true },
        { key: "district", label: "District" },
        { key: "outlet_format", label: "Outlet format", type: "select", options: OUTLET_FORMATS, required: true },
        { key: "branch_status", label: "Stage", type: "select", options: ["Live", "Greenfield"], required: true, default: "Live" },
        { key: "launch_date", label: "Launch date", type: "date" },
        { key: "branch_head_id", label: "Branch head", type: "select", optionsFrom: "users" },
        statusField,
      ]}
    />
  );
}

export function DepartmentsPage() {
  const { can } = useAuth();
  const src = can("users", "read") ? [{ name: "users", endpoint: "users", valueKey: "user_id", labelKey: "full_name" }] : [];
  const lookups = useLookups(src);
  return (
    <MasterCrud
      title="Departments" subtitle="Departments across Sales, Aftersales and Support verticals"
      endpoint="departments" idField="dept_id" lookups={lookups}
      columns={[
        { key: "dept_name", label: "Department" },
        { key: "vertical", label: "Vertical", render: (r) => <StatusBadge value={r.vertical} /> },
        { key: "functional_head_id", label: "Functional head", render: (r, l) => l.usersMap?.[r.functional_head_id] || "—" },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      ]}
      fields={[
        { key: "dept_name", label: "Department name", required: true, fullWidth: true },
        { key: "vertical", label: "Vertical", type: "select", options: VERTICALS, required: true },
        { key: "functional_head_id", label: "Functional head", type: "select", optionsFrom: "users" },
        statusField,
      ]}
    />
  );
}

export function DesignationsPage() {
  const lookups = useLookups([{ name: "departments", endpoint: "departments", valueKey: "dept_id", labelKey: "dept_name" }]);
  const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);
  return (
    <MasterCrud
      title="Designations" subtitle="Designations with grades, salary bands and approval routes"
      endpoint="designations" idField="desig_id" lookups={lookups}
      columns={[
        { key: "desig_name", label: "Designation" },
        { key: "dept_id", label: "Department", render: (r, l) => l.departmentsMap?.[r.dept_id] || "—" },
        { key: "grade", label: "Grade" },
        { key: "salary_band_min", label: "Band min", align: "right", render: (r) => inr(r.salary_band_min) },
        { key: "salary_band_max", label: "Band max", align: "right", render: (r) => inr(r.salary_band_max) },
        { key: "is_revenue_facing", label: "Revenue-facing", render: (r) => (r.is_revenue_facing ? <StatusBadge value="Yes" /> : <span className="text-[#6B7280]">No</span>) },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
      ]}
      fields={[
        { key: "desig_name", label: "Designation name", required: true },
        { key: "dept_id", label: "Department", type: "select", optionsFrom: "departments", required: true },
        { key: "grade", label: "Grade", type: "select", options: GRADES, required: true },
        { key: "salary_band_min", label: "Salary band min (₹)", type: "number" },
        { key: "salary_band_max", label: "Salary band max (₹)", type: "number" },
        { key: "is_revenue_facing", label: "Revenue-facing", type: "checkbox", checkLabel: "Directly drives revenue" },
        { key: "approval_route", label: "Approval route", fullWidth: true, placeholder: "BH > ZH > VP > HR > AMD" },
        statusField,
      ]}
    />
  );
}

export function UsersPage() {
  const { can } = useAuth();
  const lookups = useLookups([
    { name: "branches", endpoint: "branches", valueKey: "branch_name", labelKey: "branch_name" },
    { name: "departments", endpoint: "departments", valueKey: "dept_name", labelKey: "dept_name" },
    { name: "zones", endpoint: "zones", valueKey: "zone_name", labelKey: "zone_name" },
    { name: "users", endpoint: "users", valueKey: "user_id", labelKey: "full_name" },
  ]);
  const scopes = (r) => {
    const parts = [];
    if (r.zone_scope?.length) parts.push(`Zone: ${r.zone_scope.join(", ")}`);
    if (r.branch_scope?.length) parts.push(`Branch: ${r.branch_scope.join(", ")}`);
    if (r.dept_scope?.length) parts.push(`Dept: ${r.dept_scope.join(", ")}`);
    if (r.vertical_scope?.length) parts.push(`Vertical: ${r.vertical_scope.join(", ")}`);
    return parts.length ? parts.join(" · ") : "All / N.A.";
  };
  return (
    <MasterCrud
      title="Users & Roles" subtitle="Role-based access with branch, department, zone and vertical scopes"
      endpoint="users" idField="user_id" lookups={lookups}
      canWriteOverride={can("users", "write")}
      columns={[
        { key: "full_name", label: "Name" },
        { key: "employee_code", label: "Emp code" },
        { key: "email", label: "Email" },
        { key: "role", label: "Role", render: (r) => <span className="text-xs font-medium text-[#0E2240] bg-[#F5F6F8] border border-gray-200 rounded-sm px-2 py-0.5">{r.role}</span> },
        { key: "scopes", label: "Scope", render: scopes },
        { key: "is_active", label: "Status", render: (r) => <StatusBadge value={r.is_active ? "Active" : "Inactive"} /> },
      ]}
      fields={[
        { key: "full_name", label: "Full name", required: true },
        { key: "employee_code", label: "Employee code" },
        { key: "email", label: "Email", required: true },
        { key: "mobile", label: "Mobile (WhatsApp)" },
        { key: "role", label: "Role", type: "select", options: ROLES, required: true },
        { key: "password", label: "Password", type: "password", placeholder: "Default: Hire360@2026" },
        { key: "branch_scope", label: "Branch scope", type: "multiselect", optionsFrom: "branches", fullWidth: true },
        { key: "dept_scope", label: "Department scope", type: "multiselect", optionsFrom: "departments", fullWidth: true },
        { key: "zone_scope", label: "Zone scope", type: "multiselect", optionsFrom: "zones", fullWidth: true },
        { key: "vertical_scope", label: "Vertical scope", type: "multiselect", options: VERTICALS, fullWidth: true },
        { key: "reports_to_id", label: "Reports to", type: "select", optionsFrom: "users" },
        { key: "delegate_id", label: "Delegate", type: "select", optionsFrom: "users" },
        { key: "delegate_valid_from", label: "Delegate from", type: "date" },
        { key: "delegate_valid_to", label: "Delegate to", type: "date" },
        { key: "is_active", label: "Active", type: "checkbox", checkLabel: "User can sign in", default: true },
      ]}
    />
  );
}
